import React, { useMemo, useState } from "react";

import { PointSymbolProps, ResponsiveLine } from "@nivo/line";
import { compareAsc, format, isSameWeek } from "date-fns";
import { mean } from "lodash";

import {
  MadReportFullFragment,
  TemplateQuestionFragment,
  UserMadFullFragment,
} from "../../graphql/fragments";
import useTranslations from "../../hooks/useTranslations";

import { getSkillsColor } from "../../utils/colors";

import { CheckCircleIcon } from "../icons";
import { Badge, H4, H5, Span } from "../wombat-ui";

type CollaboratorAnalyticsSectionProps = {
  user: UserMadFullFragment;
};

function getDataFromReports(
  talentId: string,
  reports: MadReportFullFragment[]
) {
  const data = [];

  reports.map((report) => {
    report.reportAnswers.map((rA) => {
      if (rA.isTalent) return;
      if (
        rA.type === "ReportAnswerSkill" ||
        rA.type === "ReportAnswerEvaluation"
      ) {
        const itemId =
          rA.type === "ReportAnswerSkill"
            ? rA.templateQuestion?.subject?.name
            : rA.templateQuestion?.label;
        let mapIndex = data.findIndex((i) => i?.id === itemId);
        if (mapIndex < 0)
          mapIndex =
            data.push({
              id: itemId,
              ...(rA.type === "ReportAnswerSkill"
                ? { kind: rA?.templateQuestion?.subject?.kind }
                : {}),
              templateQuestion: rA.templateQuestion,
              data: [],
            }) - 1;
        const range = data[mapIndex].data.findIndex((d) =>
          isSameWeek(new Date(d.x), new Date(report.checkedAt))
        );

        if (range < 0)
          data[mapIndex].data.push({
            gradeCount: 1,
            comments:
              rA.value.comment !== ""
                ? [
                    {
                      username: rA.user.name,
                      comment: rA.value.comment,
                    },
                  ]
                : [],
            x: format(new Date(report.checkedAt), "yyyy-MM-dd"),
            y: parseInt(rA.value.selector as string, 10),
          });
        else {
          data[mapIndex].data[range].gradeCount += 1;
          rA.value.comment !== "" &&
            data[mapIndex].data[range].comments.push({
              username: rA.user.name,
              comment: rA.value.comment,
            });
          data[mapIndex].data[range].y = mean([
            parseInt(rA.value.selector as string, 10),
            data[mapIndex].data[range].y,
          ]);
        }
      }
    });
  });

  return data.map((d) => ({
    ...d,
    data: d.data.sort((i, j) => compareAsc(new Date(i.x), new Date(j.x))),
  }));
}

function getTickLabel(
  value: number,
  templateQuestion: TemplateQuestionFragment
): string {
  return templateQuestion?.options?.[value]?.label;
}

const CustomSymbol: React.FC<Readonly<PointSymbolProps>> = ({
  size,
  color,
  borderWidth,
  borderColor,
}) => (
  <g>
    <circle
      fill="#fff"
      r={size / 2}
      strokeWidth={borderWidth}
      stroke={borderColor}
    />
    <circle
      r={size / 5}
      strokeWidth={borderWidth}
      stroke={borderColor}
      fill={color}
      fillOpacity={0.35}
    />
  </g>
);

const CollaboratorAnalyticsSection: React.FC<CollaboratorAnalyticsSectionProps> = ({
  user,
}) => {
  const t = useTranslations();

  const chartData = useMemo(
    () =>
      getDataFromReports(
        user.id,
        user.madsAsTalent.flatMap((mad) => mad.reports)
      ),
    [user]
  );

  const [selectedData, setSelectedData] = useState([chartData?.[0] || {}]);

  return (
    <div className="w-full overflow-auto min-w-xl">
      <div className="flex flex-col">
        <H4>{t("mads.skillEvolution")}</H4>

        <div className="flex flex-wrap pt-1 space-x-2">
          {chartData.length > 0 && [
            ...chartData.map((e) => (
              <Badge
                role="button"
                key={e.id}
                className="flex items-center"
                variant={
                  e.templateQuestion?.subject?.kind
                    ? getSkillsColor(e.templateQuestion.subject.kind)
                    : "danger"
                }
                onClick={() => {
                  setSelectedData([e]);
                }}
              >
                {e.id}
                {selectedData[0]?.id === e.id && (
                  <CheckCircleIcon className="ml-2" iconClassName="w-4 h-4" />
                )}
              </Badge>
            )),
          ]}
        </div>
      </div>
      {chartData.length > 0 && (
        <div className="px-3 pt-6 h-96">
          <ResponsiveLine
            margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
            animate
            enableSlices="x"
            data={selectedData}
            xScale={{
              type: "time",
              format: "%Y-%m-%d",
              useUTC: false,
              precision: "day",
            }}
            xFormat="time:%Y-%m-%d"
            yScale={{
              type: "linear",
            }}
            axisLeft={{
              legend: `Compétences`,
              tickValues: [0, 1, 2, 3, 4],
              format: (value) => {
                return getTickLabel(
                  value as number,
                  selectedData[0]?.templateQuestion
                );
              },
              legendOffset: 12,
            }}
            axisBottom={{
              format: "%B",
              tickValues: "every 1 month",
            }}
            sliceTooltip={({ slice }) => {
              return (
                <div className="px-4 py-3 bg-white shadow-md text-dark-100">
                  {slice.points.map((point, index) => {
                    return (
                      <React.Fragment key={point.id}>
                        {index === 0 && (
                          <H4>
                            <strong>{point.data.xFormatted}</strong>
                          </H4>
                        )}
                        <div className="py-1">
                          {point.data.comments.map(
                            ({ username, comment }, index) =>
                              comment !== "" && (
                                <div
                                  className="flex flex-col px-3 py-4 bg-light-0"
                                  key={index}
                                >
                                  <H5>{t("reports.comments")}: </H5>
                                  <p className="pt-1 text-sm text-dark-100">
                                    {comment}
                                  </p>
                                  <Span className="text-xs">{username}</Span>
                                </div>
                              )
                          )}
                          {point.data.comments.length === 0 && (
                            <Span className="text-xs">
                              {t("reports.noComments")}
                            </Span>
                          )}
                        </div>
                        <div className="pt-2 text-xs text-light-900">
                          {t("mads.notationNumber", {
                            count: point.data.gradeCount,
                          })}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            }}
            curve="monotoneX"
            enablePointLabel={true}
            pointSymbol={CustomSymbol}
            pointSize={16}
            pointBorderWidth={1}
            pointBorderColor={{
              from: "color",
              modifiers: [["darker", 0.3]],
            }}
            useMesh={true}
          />
        </div>
      )}
      {chartData.length === 0 && (
        <p className="text-sm text-light-900">{t("users.noSkills")}</p>
      )}
    </div>
  );
};

export default CollaboratorAnalyticsSection;
